import { useCallback } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CartIcon, CheckIcon } from "@shopify/polaris-icons";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "hooks";
import { RootState } from "configureStore";
import {
  addFileThunk,
  removeItemThunk,
  fetchCartThunk,
} from "./documentCart.slice";
import { CART_SIZE_LIMIT_LABEL } from "./document-cart.service";
import "./document-cart.scss";

interface AddToCartButtonProps {
  fileId: string;
  /**
   * `chip` — compact highlighted control for tables (default)
   * `prominent` — larger CTA for file detail / drawers
   */
  variant?: "chip" | "prominent";
  className?: string;
  /** Matches documents vs profile visibility when adding. */
  source?: "document" | "profile";
}

const AddToCartButton: React.FC<AddToCartButtonProps> = ({
  fileId,
  variant = "chip",
  className = "",
  source = "document",
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId } = useParams<{ companyId: string }>();
  const { fileIdsInCart, items, mutating } = useAppSelector(
    (state: RootState) => state.documentCartReducer
  );
  const inCart = !!fileIdsInCart[fileId];

  const handleClick = useCallback(
    async (e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (!companyId || !fileId) return;

      if (inCart) {
        let item = items.find((i) => i.fileId === fileId);
        if (!item) {
          const result = await dispatch(fetchCartThunk(companyId)).unwrap();
          item = result.items.find((i) => i.fileId === fileId);
        }
        if (item) {
          try {
            await dispatch(
              removeItemThunk({ companyId, itemId: item.id })
            ).unwrap();
            toast.success(t("document_cart_removed"));
          } catch {
            toast.error(t("document_cart_remove_failed"));
            dispatch(fetchCartThunk(companyId));
          }
        }
        return;
      }

      try {
        const result = await dispatch(
          addFileThunk({ companyId, fileId, source })
        ).unwrap();
        if (result.truncated) {
          toast.message(
            t("document_cart_added_children_partial", {
              count: result.skippedChildren,
            })
          );
        } else if (result.alreadyInCart) {
          toast.message(t("document_cart_already_in"));
        } else {
          toast.success(t("document_cart_added"));
        }
      } catch (code) {
        if (String(code).includes("CART_FULL")) {
          toast.error(t("document_cart_full"));
        } else if (
          String(code).includes("CART_TOO_LARGE") ||
          String(code).includes("ZIP_TOO_LARGE")
        ) {
          toast.error(t("document_cart_too_large", { limit: CART_SIZE_LIMIT_LABEL }));
        } else if (String(code).includes("FILE_TOO_LARGE")) {
          toast.error(t("document_cart_file_too_large", { limit: CART_SIZE_LIMIT_LABEL }));
        } else if (String(code).includes("FILE_SIZE_UNKNOWN")) {
          toast.error(t("document_cart_file_size_unknown"));
        } else {
          toast.error(t("document_cart_add_failed"));
        }
      }
    },
    [companyId, fileId, source, inCart, items, dispatch, t]
  );

  const Icon = inCart ? CheckIcon : CartIcon;
  const label = inCart ? t("document_cart_in_cart") : t("document_cart_add");
  const sizeClass =
    variant === "prominent"
      ? "document-cart-action--prominent"
      : "document-cart-action--chip";

  return (
    <button
      type="button"
      className={`document-cart-action ${sizeClass} ${
        inCart ? "document-cart-action--active" : ""
      } ${className}`}
      disabled={mutating}
      onClick={handleClick}
      title={label}
      aria-label={label}
      aria-pressed={inCart}
    >
      <Icon className="document-cart-action__icon" aria-hidden />
      <span className="document-cart-action__label">{label}</span>
    </button>
  );
};

export default AddToCartButton;
